import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_entertainment-hub-187/artifacts/jgyw0hiu_1000010836.png";
const SHOWREEL = "/showreel.mp4";

const socials = [
  {
    label: "Facebook",
    href: "https://www.facebook.com/p/Robust-Entertainment-61571961767611/",
    svg: (
      <svg viewBox="0 0 24 24" className="w-9 h-9" aria-hidden="true">
        <circle cx="12" cy="12" r="12" fill="#1877F2" />
        <path d="M15.9 15.2l.5-3.2h-3.1V9.9c0-.9.4-1.7 1.8-1.7h1.4V5.4s-1.3-.2-2.5-.2c-2.5 0-4.2 1.5-4.2 4.3V12H7v3.2h2.8V23a11 11 0 003.5 0v-7.8h2.6z" fill="#fff" />
      </svg>
    ),
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/robust_entertainment_ltd/",
    svg: (
      <svg viewBox="0 0 24 24" className="w-9 h-9" aria-hidden="true">
        <defs>
          <radialGradient id="ig-grad" cx="30%" cy="107%" r="150%">
            <stop offset="0%" stopColor="#fdf497" />
            <stop offset="5%" stopColor="#fdf497" />
            <stop offset="45%" stopColor="#fd5949" />
            <stop offset="60%" stopColor="#d6249f" />
            <stop offset="90%" stopColor="#285AEB" />
          </radialGradient>
        </defs>
        <rect width="24" height="24" rx="6" fill="url(#ig-grad)" />
        <rect x="5" y="5" width="14" height="14" rx="4.5" fill="none" stroke="#fff" strokeWidth="1.6" />
        <circle cx="12" cy="12" r="3.4" fill="none" stroke="#fff" strokeWidth="1.6" />
        <circle cx="16.6" cy="7.4" r="1.05" fill="#fff" />
      </svg>
    ),
  },
];

const highlights = [
  "20 Years Proven Delivery",
  "Complete Entertainment Service",
];

export const Hero = () => {
  return (
    <section id="home" className="relative pt-28 pb-8 lg:pt-32 lg:pb-10 grain overflow-hidden">
      <div className="absolute -top-40 left-1/4 w-[600px] h-[600px] rounded-full bg-[#FF2A2A]/10 blur-3xl pointer-events-none" />
      <div className="absolute top-1/3 -right-40 w-[500px] h-[500px] rounded-full bg-zinc-500/10 blur-3xl pointer-events-none" />

      <div className="relative max-w-5xl mx-auto px-6 lg:px-10 flex flex-col items-center text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="flex flex-col items-center"
        >
          <img src={LOGO} alt="Robust Entertainment logo" className="h-56 sm:h-72 lg:h-80 w-auto mb-6" data-testid="hero-logo" />
          <div className="flex items-center justify-center gap-5 mb-12" data-testid="hero-socials">
            {socials.map((s) => (
              <a
                key={s.label}
                href={s.href}
                aria-label={s.label}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`hero-social-${s.label.toLowerCase()}`}
                className="hover:scale-110 transition-transform duration-200"
              >
                {s.svg}
              </a>
            ))}
          </div>
          <h1 className="font-heading heading-fluid font-semibold tracking-normal leading-[1.3] glow-silver">
            <span className="text-metal whitespace-nowrap">Complete Entertainment Solutions.</span>
            <br />
            <span className="text-white whitespace-nowrap">One Proven Company.</span>
            <br />
            <span className="text-gold whitespace-nowrap glow-gold">20 Years of Spectacular Results.</span>
          </h1>
          <p className="mt-8 text-lg md:text-xl text-zinc-200 leading-relaxed max-w-3xl mx-auto">
            From character creation and live production shows to cabaret acts, visual content, team training, technical
            sound, lighting and LED screen installations, Robust Entertainment delivers the complete entertainment
            package for holiday centres, venues and leisure brands across the UK.
          </p>
          <div className="mt-6 max-w-3xl mx-auto">
            <p className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-gold font-semibold text-xl sm:text-2xl glow-gold leading-tight">
              <span>Trusted entertainment partner to</span>
              <img
                src="https://customer-assets-lqy194kg.emergentagent.net/job_entertainment-hub-187/artifacts/42jxduiw_Phuk_logo.gif"
                alt="Park Holidays UK"
                className="inline-block h-10 sm:h-12 lg:h-14 w-auto align-middle bg-white rounded-lg px-2 py-1"
                data-testid="hero-park-holidays-logo"
              />
              <span>for 20 years.</span>
            </p>
            <p className="mt-3 text-base sm:text-lg text-zinc-200 leading-relaxed">
              Two decades of experience delivering exceptional entertainment across some of the UK's leading holiday centres.
            </p>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="relative w-full max-w-4xl mt-12"
          >
            <div data-testid="hero-video" className="relative rounded-2xl p-[2px] bg-gradient-to-br from-[#F0F0F0] via-[#8a8a92] to-[#3d3d44] shadow-2xl">
              <div className="rounded-2xl overflow-hidden">
                <video
                  src={SHOWREEL}
                  autoPlay
                  muted
                  loop
                  playsInline
                  controls
                  className="w-full h-auto block"
                />
              </div>
            </div>
            <p className="mt-4 text-center text-white font-medium text-sm sm:text-base tracking-wide">
              Watch Robust Entertainment in Action
            </p>
          </motion.div>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <a
              href="https://wa.me/447539528505"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-primary-cta"
              className="btn-glow-red bg-[#FF2A2A] hover:bg-[#FF4D4D] text-white font-medium px-8 py-4 rounded-full transition-all duration-300"
            >
              Book a Discovery Call
            </a>
            <a
              href="#services"
              data-testid="hero-secondary-cta"
              className="border border-zinc-400 hover:border-[#D4AF37] text-white font-medium px-8 py-4 rounded-full transition-colors duration-300"
            >
              View Our Services
            </a>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="relative w-full mt-8"
        >
          <ul className="grid sm:grid-cols-2 gap-3 max-w-2xl mx-auto" data-testid="hero-highlights">
            {highlights.map((h) => (
              <li key={h} className="flex flex-col items-center justify-center gap-2 text-center bg-[#121212] border border-white/10 rounded-xl px-4 py-4">
                <CheckCircle2 size={18} className="text-[#D4AF37] shrink-0" />
                <span className="text-base font-medium text-white">{h}</span>
              </li>
            ))}
          </ul>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
